import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import {
  ShieldCheck, CheckCircle2, AlertTriangle, Link2, PenLine, EyeOff, Landmark, MapPin, CalendarDays, KeyRound,
} from "lucide-react";

const CodeEntry = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.get(`/public/witness-code/${encodeURIComponent(code.trim())}`);
      navigate(`/witness/${r.data.token}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Witness code not found.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wyv-container py-12 sm:py-16 max-w-xl">
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="witness-code-entry-title">
        Witness a Declaration
      </h1>
      <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
        Someone asked you to witness their WitnessYourVote declaration? Enter the 6-character witness
        code from their screen or receipt. You will never be asked to see their ballot.
      </p>
      <Card className="mt-6">
        <CardContent className="p-6">
          <form onSubmit={lookup} className="space-y-4">
            <div>
              <Label htmlFor="w-code">Witness code</Label>
              <Input id="w-code" className="mt-1.5 font-mono text-lg tracking-widest uppercase" placeholder="ABC-123"
                     value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                     maxLength={7} data-testid="witness-code-input" />
            </div>
            <Button type="submit" className="w-full h-11" disabled={busy || code.trim().length < 6} data-testid="witness-code-submit">
              <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Looking up…" : "Open Witness Page"}
            </Button>
          </form>
          <p className="text-[11px] text-muted-foreground mt-4">
            If you received a witness link or QR code instead, just open it directly.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default function WitnessPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ witness_name: "", witness_email: "", typed_signature: "", attestation_accepted: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/public/witness/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This witness link is not valid."));
  }, [token]);

  if (!token) return <CodeEntry />;

  const sign = async (e) => {
    e.preventDefault();
    if (!form.attestation_accepted) {
      toast.error("Please confirm the attestation checkbox");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/public/witness/${token}/sign`, { ...form, website: "" });
      setResult(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="wyv-container py-16 max-w-xl">
        <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-start gap-2" data-testid="witness-link-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </CardContent></Card>
      </div>
    );
  }
  if (!info) {
    return <div className="wyv-container py-16 max-w-xl space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;
  }

  if (result) {
    return (
      <div className="wyv-container py-12 max-w-xl">
        <Card className="border-emerald-200" data-testid="witness-success-card">
          <CardContent className="p-6 sm:p-8">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-700" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground">Thank you for witnessing</h1>
            <ul className="mt-5 space-y-2 text-sm text-foreground">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Voter declaration signed</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Witness attestation signed</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Date and time recorded</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Record integrity protected</li>
              {result.mutual && (
                <li className="flex items-center gap-2 text-blue-800 font-semibold" data-testid="witness-mutual-row">
                  <Link2 className="h-4 w-4" /> Mutually Witnessed
                </li>
              )}
            </ul>
            <p className="text-sm text-muted-foreground mt-4" data-testid="witness-success-status">
              {result.declaration_number} is now: <span className="font-semibold text-foreground">{result.witness_level_label}</span>
            </p>

            {result.you_have_awaiting_declaration && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mt-6" data-testid="witness-return-note">
                <p className="text-sm text-blue-900 font-medium">Your own declaration is still awaiting a witness.</p>
                <p className="text-xs text-blue-800 mt-1">
                  Open your declaration page (from your receipt or email) and share your witness link — the person
                  you just witnessed can witness yours back. Then both records become Mutually Witnessed.
                </p>
              </div>
            )}
            {!result.you_have_declaration && (
              <div className="rounded-lg bg-secondary border border-slate-200 p-4 mt-6" data-testid="witness-create-own-note">
                <p className="text-sm text-foreground font-medium">Voted too? Create your own declaration.</p>
                <p className="text-xs text-muted-foreground mt-1">Then the person you just witnessed can witness yours — and both records become Mutually Witnessed.</p>
                <Button asChild size="sm" className="mt-3 bg-accent hover:bg-accent/90" data-testid="witness-create-own-cta">
                  <Link to="/declare">Create My Declaration</Link>
                </Button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-5">{result.email_verification_required_note}</p>
            <p className="text-[11px] text-muted-foreground mt-2">{result.disclaimer}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-xl">
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="witness-page-title">
        Witness This Declaration
      </h1>

      {info.is_test && (
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 mt-3">TEST DECLARATION</Badge>
      )}

      {/* Identity summary — minimal information only */}
      <Card className="mt-5" data-testid="witness-identity-card">
        <CardContent className="p-6">
          <p className="font-heading font-semibold text-lg text-foreground" data-testid="witness-voter-name">{info.voter_name}</p>
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            <p className="flex items-center gap-2"><Landmark className="h-3.5 w-3.5" /> {info.municipality}</p>
            {info.ward && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {info.ward}</p>}
            <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Declaration {info.declaration_number}</p>
            <p className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" /> Signed {info.signed_date ? new Date(info.signed_date).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : "—"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Ballot privacy explanation */}
      <Card className="mt-4 bg-secondary border-slate-200" data-testid="witness-ballot-note">
        <CardContent className="p-5 flex items-start gap-3">
          <EyeOff className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-foreground leading-relaxed">{info.ballot_privacy_note}</p>
        </CardContent>
      </Card>

      {info.already_witnessed ? (
        <Card className="mt-4"><CardContent className="p-5 text-sm text-muted-foreground" data-testid="witness-already-done">
          This declaration has already been witnessed. Thank you!
        </CardContent></Card>
      ) : (
        <Card className="mt-4" data-testid="witness-attestation-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-base text-foreground mb-2">Witness Attestation</h2>
            <p className="text-sm text-foreground leading-relaxed border-l-2 border-accent pl-3" data-testid="attestation-text">
              {info.attestation_text}
            </p>
            <form onSubmit={sign} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="w-name">Your full name</Label>
                <Input id="w-name" className="mt-1.5" value={form.witness_name}
                       onChange={(e) => setForm({ ...form, witness_name: e.target.value })}
                       data-testid="witness-name-input" autoComplete="name" />
              </div>
              <div>
                <Label htmlFor="w-email">Your email</Label>
                <Input id="w-email" type="email" className="mt-1.5" value={form.witness_email}
                       onChange={(e) => setForm({ ...form, witness_email: e.target.value })}
                       data-testid="witness-email-input" autoComplete="email" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Kept private. Used only to connect your own declaration (for Mutual Witness) and prevent misuse.
                </p>
              </div>
              <div>
                <Label htmlFor="w-sig">Electronic signature (type your full name)</Label>
                <Input id="w-sig" className="mt-1.5 font-serif italic" value={form.typed_signature}
                       onChange={(e) => setForm({ ...form, typed_signature: e.target.value })}
                       data-testid="witness-signature-input" placeholder="Type your full name as your signature" />
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox
                  checked={form.attestation_accepted}
                  onCheckedChange={(v) => setForm({ ...form, attestation_accepted: !!v })}
                  className="mt-0.5"
                  data-testid="witness-attest-checkbox"
                />
                <span className="text-sm text-foreground">I attest that this information is true to the best of my knowledge.</span>
              </label>
              <Button
                type="submit"
                className="w-full h-12 text-base bg-accent hover:bg-accent/90"
                disabled={busy || !form.witness_name.trim() || !form.witness_email.trim() || !form.typed_signature.trim() || !form.attestation_accepted}
                data-testid="sign-as-witness-button"
              >
                <PenLine className="h-4 w-4 mr-2" /> {busy ? "Signing…" : "SIGN AS WITNESS"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-6" data-testid="witness-disclaimer">
        {info.disclaimer}
      </p>
    </div>
  );
}
